import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SupportAssistant from '../components/SupportAssistant';
import { 
  CheckCircle, X, ArrowRight, Zap, Shield, Brain,
  FileText, Scan, Users, Euro, Clock, Award, Star,
  Sparkles, TrendingUp, Rocket, Check, Crown, Heart,
  ChevronRight, Gift
} from 'lucide-react';

const competitors = [
  {
    name: "SmartFacture AI",
    highlight: true,
    price: "14€",
    priceDetail: "/mois",
    features: {
      scanner_ia: true,
      facturx: true,
      ppf_simulation: true,
      conformite_score: true,
      essai_gratuit: "7 jours",
      factures_illimitees: "Dès 29€/mois",
      support: "Email + Assistant IA",
      ia_integree: true,
      multi_utilisateurs: "Plan Expert",
      export_fec: "Inclus",
      setup: "2 minutes",
      engagement: "Sans engagement"
    }
  },
  {
    name: "Pennylane",
    price: "14€",
    priceDetail: "/mois (min)",
    features: {
      scanner_ia: false,
      facturx: true,
      ppf_simulation: false,
      conformite_score: false,
      essai_gratuit: "14 jours",
      factures_illimitees: "Selon plan",
      support: "Email",
      ia_integree: false,
      multi_utilisateurs: "Oui",
      export_fec: "Oui",
      setup: "Long",
      engagement: "Annuel"
    }
  },
  {
    name: "Tiime",
    price: "Gratuit",
    priceDetail: "(limité)",
    features: {
      scanner_ia: false,
      facturx: true,
      ppf_simulation: false,
      conformite_score: false,
      essai_gratuit: "Gratuit limité",
      factures_illimitees: "Payant",
      support: "Email",
      ia_integree: false,
      multi_utilisateurs: "Non",
      export_fec: "Non",
      setup: "Moyen",
      engagement: "Sans"
    }
  },
  {
    name: "Sellsy",
    price: "29€",
    priceDetail: "/mois (min)",
    features: {
      scanner_ia: false,
      facturx: true,
      ppf_simulation: false,
      conformite_score: false,
      essai_gratuit: "15 jours",
      factures_illimitees: "Selon plan",
      support: "Email + Tél",
      ia_integree: false,
      multi_utilisateurs: "Oui",
      export_fec: "Oui",
      setup: "Long",
      engagement: "Annuel"
    }
  },
  {
    name: "Axonaut",
    price: "35€",
    priceDetail: "/mois",
    features: {
      scanner_ia: false,
      facturx: true,
      ppf_simulation: false,
      conformite_score: false,
      essai_gratuit: "15 jours",
      factures_illimitees: "Oui",
      support: "Email + Tél",
      ia_integree: false,
      multi_utilisateurs: "Oui",
      export_fec: "Oui",
      setup: "Moyen",
      engagement: "Annuel"
    }
  }
];

const featureLabels = {
  scanner_ia: { label: "Scanner IA (OCR)", icon: Scan, exclusive: true },
  ia_integree: { label: "IA intégrée", icon: Brain, exclusive: true },
  ppf_simulation: { label: "Simulation PPF/PDP", icon: Shield, exclusive: true },
  conformite_score: { label: "Score de conformité", icon: Award, exclusive: true },
  facturx: { label: "Format Factur-X", icon: FileText },
  essai_gratuit: { label: "Essai gratuit", icon: Clock },
  factures_illimitees: { label: "Factures illimitées", icon: Zap },
  support: { label: "Support", icon: Users },
  multi_utilisateurs: { label: "Multi-utilisateurs", icon: Users },
  export_csv: { label: "Export comptable CSV", icon: FileText },
  setup: { label: "Mise en route", icon: Rocket },
  engagement: { label: "Engagement", icon: Euro }
};

const renderValue = (value, isHighlight) => {
  if (value === true) {
    return (
      <div className="flex items-center justify-center">
        <CheckCircle className={`h-5 w-5 ${isHighlight ? 'text-emerald-500' : 'text-emerald-400'}`} />
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex items-center justify-center">
        <X className="h-5 w-5 text-slate-300" />
      </div>
    );
  }
  return (
    <span className={`text-sm ${isHighlight ? 'font-medium text-slate-700' : 'text-slate-500'}`}>
      {value}
    </span>
  );
};

const ComparaisonPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-white to-sky-50/30">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-sky-100 py-4 px-4 sm:px-6 lg:px-8 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-sky-200">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800" style={{fontFamily: "'Outfit', sans-serif"}}>
              SmartFacture AI
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/pricing" className="text-slate-600 hover:text-sky-600 transition-colors hidden sm:block font-medium">
              Tarifs
            </Link>
            <Link to="/register">
              <Button className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6 shadow-md shadow-sky-200 hover:shadow-lg transition-all">
                Essai gratuit
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Trust badges */}
          <div className="flex justify-center gap-3 mb-8 flex-wrap">
            <span className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-medium">
              <Crown className="h-4 w-4" />
              #1 en IA
            </span>
            <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
              <TrendingUp className="h-4 w-4" />
              Le moins cher
            </span>
            <span className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium">
              <Rocket className="h-4 w-4" />
              Prêt en 2 min
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-6 leading-tight" style={{fontFamily: "'Playfair Display', serif"}}>
            La solution e-facturation
            <br />
            <span className="text-sky-600">simple et abordable</span>
          </h1>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-12 leading-relaxed">
            Comparez SmartFacture AI avec les autres solutions du marché 
            et découvrez pourquoi nos clients nous choisissent.
          </p>

          {/* Key metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { value: "2 min", label: "Pour démarrer", color: "sky" },
              { value: "14€", label: "Par mois", color: "emerald" },
              { value: "100%", label: "Conforme 2026", color: "violet" },
              { value: "IA", label: "Incluse", color: "amber" }
            ].map((stat, idx) => (
              <div key={idx} className={`bg-${stat.color}-50 border border-${stat.color}-100 rounded-2xl p-5 hover:shadow-md transition-shadow`}>
                <p className={`text-3xl font-bold text-${stat.color}-600 mb-1`}>{stat.value}</p>
                <p className="text-sm text-slate-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why SmartFacture - Clean Cards */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sky-600 font-semibold mb-2">Nos avantages</p>
            <h2 className="text-3xl font-bold text-slate-800" style={{fontFamily: "'Playfair Display', serif"}}>
              Ce qui nous rend unique
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <Card className="border-0 shadow-lg shadow-sky-100/50 hover:shadow-xl transition-shadow bg-gradient-to-b from-white to-sky-50/30 rounded-3xl overflow-hidden">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center mb-6">
                  <Scan className="h-7 w-7 text-sky-600" />
                </div>
                <Badge className="bg-sky-100 text-sky-700 mb-4 font-medium">Exclusif</Badge>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Scanner IA</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Photographiez un document, notre IA extrait automatiquement toutes les données. 
                  <span className="font-medium text-slate-700"> Aucun concurrent ne l'a.</span>
                </p>
                <p className="text-sky-600 font-medium text-sm flex items-center">
                  Fini la saisie manuelle
                  <ChevronRight className="h-4 w-4 ml-1" />
                </p>
              </CardContent>
            </Card>

            {/* Card 2 */}
            <Card className="border-0 shadow-lg shadow-emerald-100/50 hover:shadow-xl transition-shadow bg-gradient-to-b from-white to-emerald-50/30 rounded-3xl overflow-hidden">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                  <Euro className="h-7 w-7 text-emerald-600" />
                </div>
                <Badge className="bg-emerald-100 text-emerald-700 mb-4 font-medium">Meilleur prix</Badge>
                <h3 className="text-xl font-bold text-slate-800 mb-3">2x Moins Cher</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  <span className="text-3xl font-bold text-emerald-600">14€</span>/mois vs 29-55€ ailleurs. 
                  <span className="font-medium text-slate-700"> Qualité identique.</span>
                </p>
                <p className="text-emerald-600 font-medium text-sm flex items-center">
                  Économisez jusqu'à 500€/an
                  <ChevronRight className="h-4 w-4 ml-1" />
                </p>
              </CardContent>
            </Card>

            {/* Card 3 */}
            <Card className="border-0 shadow-lg shadow-violet-100/50 hover:shadow-xl transition-shadow bg-gradient-to-b from-white to-violet-50/30 rounded-3xl overflow-hidden">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mb-6">
                  <Rocket className="h-7 w-7 text-violet-600" />
                </div>
                <Badge className="bg-violet-100 text-violet-700 mb-4 font-medium">Ultra simple</Badge>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Prêt en 2 Minutes</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Inscription rapide, interface intuitive. Créez votre première facture 
                  <span className="font-medium text-slate-700"> immédiatement.</span>
                </p>
                <p className="text-violet-600 font-medium text-sm flex items-center">
                  Zéro configuration complexe
                  <ChevronRight className="h-4 w-4 ml-1" />
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Table - Clean Design */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sky-600 font-semibold mb-2">Comparaison</p>
            <h2 className="text-3xl font-bold text-slate-800 mb-3" style={{fontFamily: "'Playfair Display', serif"}}>
              SmartFacture AI vs la concurrence
            </h2>
            <p className="text-slate-500">Comparez objectivement et faites le bon choix</p>
          </div>

          <Card className="border border-slate-200 shadow-xl shadow-slate-200/50 rounded-3xl overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left p-5 bg-slate-50 text-sm font-semibold text-slate-500 min-w-[180px]">
                      Fonctionnalité
                    </th>
                    {competitors.map((comp, idx) => (
                      <th key={idx} className={`p-5 text-center min-w-[120px] ${comp.highlight ? 'bg-sky-50' : 'bg-slate-50'}`}>
                        {comp.highlight && (
                          <div className="flex justify-center mb-2">
                            <Badge className="bg-sky-500 text-white text-xs">
                              <Star className="h-3 w-3 mr-1" />
                              Recommandé
                            </Badge>
                          </div>
                        )}
                        <p className={`font-bold ${comp.highlight ? 'text-sky-700' : 'text-slate-600'}`}>
                          {comp.name}
                        </p>
                        <p className={`text-2xl font-bold mt-1 ${comp.highlight ? 'text-sky-600' : 'text-slate-700'}`}>
                          {comp.price}
                        </p>
                        <p className="text-xs text-slate-400">{comp.priceDetail}</p>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(featureLabels).map(([key, { label, icon: Icon, exclusive }], ridx) => (
                    <tr key={key} className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${ridx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <Icon className={`h-4 w-4 ${exclusive ? 'text-sky-500' : 'text-slate-400'}`} />
                          <span className={`text-sm ${exclusive ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                            {label}
                          </span>
                          {exclusive && (
                            <Badge className="bg-sky-100 text-sky-600 text-xs py-0 px-2">Exclusif</Badge>
                          )}
                        </div>
                      </td>
                      {competitors.map((comp, cidx) => (
                        <td key={cidx} className={`p-4 text-center ${comp.highlight ? 'bg-sky-50/50' : ''}`}>
                          {renderValue(comp.features[key], comp.highlight)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <Card className="border border-slate-100 shadow-lg shadow-slate-100 rounded-3xl overflow-hidden bg-gradient-to-r from-sky-50 to-violet-50">
            <CardContent className="p-8 sm:p-10">
              <div className="flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                <div className="flex -space-x-3">
                  {['A', 'B', 'C', 'D', 'E'].map((letter, i) => (
                    <div key={i} className="w-11 h-11 rounded-full bg-gradient-to-br from-sky-400 to-violet-500 border-3 border-white flex items-center justify-center text-white font-bold text-sm shadow-md">
                      {letter}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center justify-center sm:justify-start gap-1 mb-2">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className="h-5 w-5 text-amber-400 fill-amber-400" />
                    ))}
                    <span className="text-slate-500 text-sm ml-2">4.9/5</span>
                  </div>
                  <p className="text-slate-700 font-medium">
                    Rejoignez <span className="text-sky-600 font-bold">+150 entrepreneurs</span> qui ont simplifié leur facturation
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Card className="border-0 shadow-2xl shadow-sky-200/50 rounded-3xl overflow-hidden bg-gradient-to-br from-sky-500 to-blue-600">
            <CardContent className="p-10 sm:p-14 text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-6">
                <Gift className="h-5 w-5 text-white" />
                <span className="text-white font-medium text-sm">Offre de lancement</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{fontFamily: "'Playfair Display', serif"}}>
                Essayez gratuitement
              </h2>
              <p className="text-sky-100 text-lg mb-8 max-w-xl mx-auto">
                7 jours d'essai gratuit, sans carte bancaire.
                Créez vos premières factures conformes en 2 minutes.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-sky-600 hover:bg-sky-50 h-14 px-10 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all" data-testid="comparison-cta">
                    Démarrer gratuitement
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-6 text-white/90 text-sm">
                {["Sans carte bancaire", "Sans engagement", "Support inclus"].map((text, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="h-4 w-4" />
                    <span>{text}</span>
                  </div>
                ))}
              </div>

              <p className="text-white/70 text-sm mt-6">
                Code promo <span className="text-white font-bold">LANCEMENT26</span> : -20% sur le premier mois
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <div className="text-center pb-12">
        <Link to="/" className="text-slate-500 hover:text-sky-600 transition-colors text-sm inline-flex items-center gap-2">
          <ArrowRight className="h-4 w-4 rotate-180" />
          Retour à l'accueil
        </Link>
      </div>

      <SupportAssistant />
    </div>
  );
};

export default ComparaisonPage;
